'use client'

import { useState } from 'react'
import { useCurrency } from '@/lib/currency'
import { payoutApi } from '@/lib/api'
import type { PayoutRequest } from '@/types'
import { CreditCard, Building2, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react'

interface PayoutHistoryProps {
  payoutRequests: PayoutRequest[]
  onRefresh?: () => void
}

export default function PayoutHistory({ payoutRequests, onRefresh }: PayoutHistoryProps) {
  const { format } = useCurrency()
  const [selectedRequest, setSelectedRequest] = useState<PayoutRequest | null>(null)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'failed'>('all')

  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null

  const filteredRequests = filter === 'all' 
    ? payoutRequests 
    : payoutRequests.filter(req => req.status === filter)

  const getStatusBadge = (status: PayoutRequest['status']) => {
    const baseClasses = "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
    
    switch (status) {
      case 'pending':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>
      case 'approved':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
          <CheckCircle className="w-3 h-3 mr-1" />
          Approved
        </span>
      case 'processing':
        return <span className={`${baseClasses} bg-purple-100 text-purple-800`}>
          <CreditCard className="w-3 h-3 mr-1" />
          Processing
        </span>
      case 'completed':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </span>
      case 'rejected':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>
          <XCircle className="w-3 h-3 mr-1" />
          Rejected
        </span>
      case 'failed':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>
          <AlertCircle className="w-3 h-3 mr-1" />
          Failed
        </span>
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Unknown</span>
    }
  }

  const handleCancel = async (requestId: string) => {
    if (!token) return
    
    if (!confirm('Are you sure you want to cancel this payout request?')) {
      return
    }

    try {
      const response = await payoutApi.cancelRequest(token, requestId)
      if (response.success) {
        onRefresh?.()
      } else {
        alert(response.error || 'Failed to cancel payout request')
      }
    } catch (error: any) {
      alert(error.message || 'Failed to cancel payout request')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (payoutRequests.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Payout History</h2>
        <div className="text-center py-12">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No payout requests yet</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Payout History</h2>
        
        {/* Filter */}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processed Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRequests.map((request) => (
              <tr key={request.id || request._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(request.requestedAt)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                  {format(request.amount)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center gap-2">
                    {request.paymentMethod === 'bank_transfer' ? (
                      <>
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span>Bank Transfer</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        <span>Stripe Connect</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {getStatusBadge(request.status)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {request.completedAt 
                    ? formatDate(request.completedAt)
                    : request.processedAt
                    ? formatDate(request.processedAt)
                    : '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedRequest(selectedRequest?.id === request.id ? null : request)}
                      className="text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      {selectedRequest?.id === request.id ? 'Hide' : 'View'}
                    </button>
                    {request.status === 'pending' && (
                      <button
                        onClick={() => handleCancel(request.id || (request as any)._id)}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Request Details */}
      {selectedRequest && (
        <div className="mt-6 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Request ID</p>
              <p className="font-medium text-gray-900">{selectedRequest.id || (selectedRequest as any)._id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Amount</p>
              <p className="font-medium text-gray-900">{format(selectedRequest.amount)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Payment Method</p>
              <p className="font-medium text-gray-900">
                {selectedRequest.paymentMethod === 'bank_transfer' ? 'Bank Transfer' : 'Stripe Connect'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
            </div>
            {selectedRequest.bankDetails && (
              <>
                <div>
                  <p className="text-sm text-gray-600">Bank Name</p>
                  <p className="font-medium text-gray-900">{selectedRequest.bankDetails.bankName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Account Name</p>
                  <p className="font-medium text-gray-900">{selectedRequest.bankDetails.accountName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Account Number</p>
                  <p className="font-medium text-gray-900">{selectedRequest.bankDetails.accountNumber}</p>
                </div>
              </>
            )}
            {selectedRequest.rejectionReason && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-600">Rejection Reason</p>
                <p className="font-medium text-red-600">{selectedRequest.rejectionReason}</p>
              </div>
            )}
            {selectedRequest.failureReason && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-600">Failure Reason</p>
                <p className="font-medium text-red-600">{selectedRequest.failureReason}</p>
              </div>
            )}
            {selectedRequest.transferId && (
              <div>
                <p className="text-sm text-gray-600">Transfer ID</p>
                <p className="font-medium text-gray-900">{selectedRequest.transferId}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

